import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import qs from 'qs';

import { API_URL, DEV_API_KEY } from 'src/config';
import { store } from 'src/store/store';
import { currentUserSlice } from 'src/store/user/slice';
import { verificationTokenInput } from 'src/utils/csrf';
import { hardRedirect } from 'src/utils/hardRedirect';
import { SafeAnyType } from 'src/utils/safeAny';

axios.defaults.baseURL = API_URL;
axios.defaults.withCredentials = true;

/** Retry request on concurrency conflict on the server side.  */
axiosRetry(axios, {
  retries: 2,
  retryDelay: (retryCount, error) => {
    // eslint-disable-next-line no-console
    console.info(`Retry request to '${error?.config.url}'. Try number: ${retryCount}.`);
    return retryCount * 100;
  },
  retryCondition: (error) => error?.response?.status === 409,
});

/** outer input */

axios.interceptors.request.use((request) => {
  if (request.method === 'post') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    request.headers.__RequestVerificationToken = verificationTokenInput?.value;
  }

  return request;
});

const STOP_EXECUTION_CONST = Symbol('stop App');

export const isStringHtml = (data: SafeAnyType) => /<\/?[a-z][\s\S]*>/i.test(data);

const stopApp = (url?: string) => {
  if (url) {
    window.stop();
    hardRedirect(url);
  } else {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    store.dispatch(currentUserSlice.actions.clearCurrentUser(true));
  }
  // Return special key to stop farther execution of js, to avoid errors with wrong data or null
  return STOP_EXECUTION_CONST;
};

axios.interceptors.response.use(
  ({ data, config, request }) => {
    const responseUrl: string = request.responseURL.trim();

    if (responseUrl.includes('/csrf/token') || responseUrl.includes('/microserver')) {
      return data;
    }

    const isResponseHtml = isStringHtml(data);

    const decodedResponse = isResponseHtml ? '' : JSON.parse(decodeURIComponent(escape(atob(data))));

    if (decodedResponse.CsrfCheckFailed || isResponseHtml) {
      return stopApp();
    }

    const encodeUrl = (url: string, params: Record<string, SafeAnyType>) => {
      const addParams = params
        ? Object.keys(params)
            .filter((key) => params[key] !== undefined && params[key] !== null)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&')
        : '';

      // trim is important
      const preparedUrl = url.trim();

      if (addParams) {
        const hasQuestionMark = preparedUrl.search(/\?/) > 0;
        return `${preparedUrl}${hasQuestionMark ? '&' : '?'}${addParams}`;
      }

      return preparedUrl;
    };

    const { params } = config;
    const requestUrl = encodeUrl(`${config.baseURL}${config.url}`, params);

    let encodedResponseUrl = responseUrl;

    if (responseUrl.search(/\?/) > 0) {
      const paramsIndex = responseUrl.indexOf('?');
      const url = responseUrl.slice(0, paramsIndex);
      const params = qs.parse(responseUrl.slice(paramsIndex + 1));

      encodedResponseUrl = encodeUrl(url, params);
    }

    if (requestUrl !== encodedResponseUrl && !requestUrl.startsWith(DEV_API_KEY)) {
      return stopApp(responseUrl);
    }

    return decodedResponse;
  },
  (error: AxiosError) => {
    // eslint-disable-next-line no-console
    console.info('http Error:', error);

    return Promise.reject(error);
  }
);

export type BaseApiFunctionType = (
  method: 'get' | 'post' | 'delete',
  url: string,
  data?: Record<string, SafeAnyType> | number,
  options?: {
    asPrimitive?: boolean;
    asParams?: boolean;
    baseUrl?: string;
  }
) => Promise<SafeAnyType>;

export type HttpApiFunctionType = (
  url: Parameters<BaseApiFunctionType>[1],
  data?: Parameters<BaseApiFunctionType>[2],
  options?: Parameters<BaseApiFunctionType>[3]
) => ReturnType<BaseApiFunctionType>;

export type ApiFunctionType<D = SafeAnyType, R = SafeAnyType> = (data: D) => Promise<R>;
export type ApiFunctionOptionalParamType<D = SafeAnyType, R = SafeAnyType> = (data?: D) => Promise<R>;
export type ApiFunctionNoParamType<R = SafeAnyType> = ApiFunctionOptionalParamType<never, R>;

export const baseApiFunction: BaseApiFunctionType = (method, url, data, options) => {
  const dataName = method === 'get' || options?.asParams ? 'params' : 'data';

  let promise: Promise<SafeAnyType>;

  if (data) {
    const { signal, ...rest } = data as Record<string, SafeAnyType>; // if not, see useRequest

    if (data instanceof FormData) {
      promise = axios({
        baseURL: options?.baseUrl,
        method,
        url,
        signal,
        [dataName]: data,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else if (options?.asPrimitive) {
      const dataAsString = Object.entries(rest)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      promise = axios({
        baseURL: options?.baseUrl,
        method,
        url,
        signal,
        [dataName]: dataAsString,
      });
    } else {
      promise = axios({
        baseURL: options?.baseUrl,
        method,
        url,
        signal,
        [dataName]: rest,
      });
    }
  } else {
    // this was called from redux-sagas
    promise = axios({
      baseURL: options?.baseUrl,
      method,
      url,
    });
  }

  return new Promise((resolve, reject) => {
    promise
      .then((response) => {
        // Ignore this special response
        if (response !== STOP_EXECUTION_CONST) {
          resolve(response);
        }
      })
      .catch(reject);
  });
};

export const httpGet: HttpApiFunctionType = (url, data, options) => baseApiFunction('get', url, data, options);

export const httpPost: HttpApiFunctionType = (url, data, options) => baseApiFunction('post', url, data, options);

export const httpDelete: HttpApiFunctionType = (url, data, options) => baseApiFunction('delete', url, data, options);

/**
 * provide just a url, whereas data will be put indirectly
 * functions below provide a more convenient way to express api calls
 *
 * @example
 * httpGetCancellable('url', options) => (data) => Promise
 *    is equivalent to
 * httpGet('url', data, options) => Promise
 * */
type HttpApiCancellableFunctionType = (
  url: Parameters<BaseApiFunctionType>[1],
  options?: Parameters<BaseApiFunctionType>[3]
) => (data?: Parameters<BaseApiFunctionType>[2]) => ReturnType<BaseApiFunctionType>;

export const httpGetCancellable: HttpApiCancellableFunctionType = (url, options) => {
  // put the url as the name for useRequest
  // eslint-disable-next-line func-names
  const f = function (data?: Parameters<BaseApiFunctionType>[2]) {
    return httpGet(url, data, options);
  };
  Object.defineProperty(f, 'name', { value: url });

  return f;
};

export const httpPostCancellable: HttpApiCancellableFunctionType = (url, options) => {
  // put the url as the name for useRequest
  // eslint-disable-next-line func-names
  const f = function (data?: Parameters<BaseApiFunctionType>[2]) {
    return httpPost(url, data, options);
  };
  Object.defineProperty(f, 'name', { value: url });

  return f;
};

export const httpDeleteCancellable: HttpApiCancellableFunctionType = (url, options) => {
  // put the url as the name for useRequest
  // eslint-disable-next-line func-names
  const f = function (data?: Parameters<BaseApiFunctionType>[2]) {
    return httpDelete(url, data, options);
  };
  Object.defineProperty(f, 'name', { value: url });

  return f;
};
